import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Flex,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    Icon,
    Box,
    Spinner,
    Badge,
} from '@chakra-ui/react';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table';
import { FaRegEdit } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { useAds } from "contexts/AdsContext";
import { useAuth } from "contexts/AuthContext";
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import Card from "components/card/Card";
import CButton from "components/CButton";
import Pagination from "components/Pagination";
import ConfirmDialog from "components/ConfirmDialog";

export default function AdsOverview({ canCreate, canRead, canUpdate, canDelete }) {
    const { getAllAds, deleteAds } = useAds();

    const textColor = useColorModeValue('navy.700', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

    const [globalFilter, setGlobalFilter] = useState('');
    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [adsToDelete, setAdsToDelete] = useState(null);
    const [filter, setFilter] = useState({
        search: '',
        limit: 10,
        page: 1
    });

    const navigate = useNavigate();

    const handleDelete = async () => {
        if (adsToDelete) {
            try {
                await deleteAds(adsToDelete);
                setAdsToDelete(null);
                fetchAds();
            } catch (error) {
                console.error("Failed to delete discount:", error);
            }
            setIsDialogOpen(false);
        }
    };

    const openDeleteDialog = (discountId) => {
        setAdsToDelete(discountId);
        setIsDialogOpen(true);
    };

    const columnHelper = createColumnHelper();
    const columns = useMemo(() => {
        const baseColumns = [
            columnHelper.accessor('title', {
                header: () => <Text color="gray.400">Title</Text>,
                cell: (info) => (
                    <Text color={textColor} fontSize="md" fontWeight="500">
                        {info.getValue()}
                    </Text>
                ),
            }),
            columnHelper.accessor('description', {
                header: () => <Text color="gray.400">DESCRIPTION</Text>,
                cell: (info) => (
                    <Text color={textColor} fontSize="md" fontWeight="500" className="ellipsis-wrap" width='500px'>
                        {info.getValue()}
                    </Text>
                ),
            }),
            columnHelper.accessor('clicks', {
                header: () => <Text color="gray.400">CLICKS</Text>,
                cell: (info) => (
                    <Text color={textColor} fontSize="md" fontWeight="500">
                        {info.getValue()}
                    </Text>
                ),
            }),
            columnHelper.accessor('is_active', {
                header: () => <Text color="gray.400">STATUS</Text>,
                cell: (info) => (
                    <Badge colorScheme={info.getValue() === 1 ? 'green' : 'red'} fontSize="sm" fontWeight="500">
                        {info.getValue() === 1 ? 'Active' : 'Inactive'}
                    </Badge>
                ),
            }),
        ];

        baseColumns.push(
            columnHelper.accessor('actions', {
                header: () => <Text color="gray.400">ACTIONS</Text>,
                cell: (info) => (
                    <Flex>
                        {canUpdate && <Icon
                            as={FaRegEdit}
                            h={6}
                            w={6}
                            mt='10px'
                            me='20px'
                            onClick={() => navigate(`/admin/ads/edit/${info.row.original.id}`)}
                            cursor='pointer'
                            title="Edit"
                            color='#fa8f03'
                        />}
                        {canDelete && <Icon
                            as={RiDeleteBin6Line}
                            h={6}
                            w={6}
                            mt='10px'
                            me='20px'
                            onClick={() => openDeleteDialog(info.row.original.id)}
                            cursor='pointer'
                            title="Delete"
                            color='#fa8f03'
                        />}
                    </Flex>
                ),
            })
        );
        return baseColumns;
    }, [canUpdate, canDelete]);

    const [{ pageIndex, pageSize }, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const pagination = useMemo(
        () => ({ pageIndex, pageSize }),
        [pageIndex, pageSize],
    );

    const table = useReactTable({
        data,
        columns,
        state: { globalFilter, pagination },
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    useEffect(() => {
        fetchAds();
    }, [filter.search, filter.limit, filter.page]);

    const fetchAds = async () => {
        setIsLoading(true);
        try {
            const data = await getAllAds({
                search: filter.search,
                limit: filter.limit,
                page: filter.page,
            });
            if (data && data.ads) {
                setData(data.ads);
                setTotalPages(data.totalPages || 0)
            } else {
                console.warn("No ads found or data is undefined");
                setData([]);
            }
        } catch (error) {
            console.error("Failed to fetch ads:", error);
            setData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (newPageIndex) => {
        setPagination((prev) => ({
            ...prev,
            pageIndex: newPageIndex,
        }));
        setFilter((prev) => ({
            ...prev,
            page: newPageIndex,
        }));
    };

    return (
        <Flex direction='column' pt={{ sm: "125px", lg: "55px" }}>
            <ConfirmDialog
                position='top'
                isOpen={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                onConfirm={handleDelete}
                header="Delete Confirmation"
                message="Are you sure you want to delete this ad?"
            />
            <Card px='0px' minHeight="80vh">
                <Flex direction="column" w="100%" overflowX="auto">
                    <Flex align="center" justify="space-between" w="100%" px="22px" mb="36px">
                        <DebouncedInput
                            value={globalFilter ?? ''}
                            onChange={(value) => setFilter((prev) => ({ ...prev, search: value, page: 1 }))}
                            placeholder="Search..."
                        />
                        <Flex gap="16px">
                            {canCreate &&
                                <CButton
                                    onClick={() => navigate('/admin/ads/create')}
                                >
                                    Add Ad
                                </CButton>
                            }
                        </Flex>
                    </Flex>
                    {isLoading ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            borderRadius="10px"
                            overflow="hidden"
                            minHeight="500px"
                        >
                            <Spinner color="#fa8f03" />
                        </Box>
                    ) : (
                        <>
                            <Box border="2px solid #fa8f03" borderRadius="10px" overflow="auto" maxHeight="520px">
                                <Table variant="simple">
                                    <Thead>
                                        {table.getHeaderGroups().map((headerGroup) => (
                                            <Tr key={headerGroup.id} bg='#fa8f03'>
                                                {headerGroup.headers.map((header) => (
                                                    <Th key={header.id} borderColor='#fa8f03'>
                                                        {header.isPlaceholder ? null : (
                                                            <Flex
                                                                onClick={header.column.getToggleSortingHandler()}
                                                                cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                                                                justify="space-between"
                                                                align="center"
                                                                color="white"
                                                            >
                                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                            </Flex>
                                                        )}
                                                    </Th>
                                                ))}
                                            </Tr>
                                        ))}
                                    </Thead>
                                    <Tbody>
                                        {data.length > 0 ? (
                                            table.getRowModel().rows.map((row) => (
                                                <Tr key={row.id}>
                                                    {row.getVisibleCells().map((cell) => (
                                                        <Td key={cell.id} fontSize="sm" borderColor='#fa8f03'>
                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                        </Td>
                                                    ))}
                                                </Tr>
                                            ))
                                        ) : (
                                            <Tr>
                                                <Td colSpan={columns.length} textAlign="center">
                                                    No ads found.
                                                </Td>
                                            </Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </Box>
                            <Pagination totalPages={totalPages} currentPage={filter.page} onPageChange={handlePageChange} />
                        </>
                    )}
                </Flex>
            </Card>
        </Flex>
    );
}

function DebouncedInput({ value: initialValue, onChange, debounce = 500, ...props }) {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value);
        }, debounce);
        return () => clearTimeout(timeout);
    }, [value, debounce]);

    return (
        <SearchBar
            value={value}
            onChange={(e) => setValue(e.target.value)}
            h="44px"
            w={{ lg: '390px' }}
            borderRadius="16px"
            {...props}
        />
    );
}
